import React from 'react';

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
	variant?:
		| 'h1'
		| 'h2'
		| 'h3'
		| 'h4'
		| 'h5'
		| 'h6'
		| 'body1'
		| 'body2'
		| 'caption'
		| 'overline'
		| 'title';
	color?:
		| 'primary'
		| 'secondary'
		| 'success'
		| 'danger'
		| 'warning'
		| 'muted'
		| 'inherit';
	weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold';
	align?: 'left' | 'center' | 'right' | 'justify';
	as?: keyof JSX.IntrinsicElements;
	truncate?: boolean;
	children: React.ReactNode;
}

const Typography: React.FC<TypographyProps> = ({
	variant = 'body1',
	color = 'inherit',
	weight = 'normal',
	align = 'left',
	as,
	truncate = false,
	className = '',
	children,
	...props
}) => {
	// Determine the HTML element to render
	const getComponent = () => {
		if (as) return as;

		switch (variant) {
			case 'h1':
				return 'h1';
			case 'h2':
				return 'h2';
			case 'h3':
				return 'h3';
			case 'h4':
				return 'h4';
			case 'h5':
				return 'h5';
			case 'h6':
				return 'h6';
			case 'title':
				return 'div';
			default:
				return 'p';
		}
	};

	// Variant styles using standard Tailwind classes with design token values
	const variantStyles = {
		h1: `text-4xl font-bold tracking-tight leading-tight`,
		h2: `text-3xl font-bold leading-tight`,
		h3: `text-2xl font-bold leading-tight`,
		h4: `text-xl font-semibold leading-snug`,
		h5: `text-lg font-semibold leading-snug`,
		h6: `text-base font-semibold leading-snug`,
		body1: `text-base leading-normal`,
		body2: `text-sm leading-normal`,
		caption: `text-xs leading-snug`,
		overline: `text-xs font-medium uppercase tracking-wider leading-none`,
		title: `text-xl leading-normal`,
	};

	// Color styles using clean utility classes
	const colorStyles = {
		primary: 'text-primary-600',
		secondary: 'text-neutral-600',
		success: 'text-success-600',
		danger: 'text-error-600',
		warning: 'text-warning-600',
		muted: 'text-neutral-500',
		inherit: '',
	};

	// Weight styles using standard Tailwind classes with design token values
	const weightStyles = {
		light: 'font-light',
		normal: 'font-normal',
		medium: 'font-medium',
		semibold: 'font-semibold',
		bold: 'font-bold',
	};

	// Alignment styles
	const alignStyles = {
		left: 'text-left',
		center: 'text-center',
		right: 'text-right',
		justify: 'text-justify',
	};

	const Component = getComponent() as any;

	return (
		<Component
			className={`
                font-primary
                ${variantStyles[variant]}
                ${colorStyles[color]}
                ${weightStyles[weight]}
                ${alignStyles[align]}
                ${truncate ? 'truncate' : ''}
                ${className}
            `
				.trim()
				.replace(/\s+/g, ' ')}
			{...props}
		>
			{children}
		</Component>
	);
};

export default Typography;

// Convenience components for common use cases
export const Heading1: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="h1" {...props} />
);

export const Heading2: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="h2" {...props} />
);

export const Heading3: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="h3" {...props} />
);

export const Heading4: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="h4" {...props} />
);

export const Body: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="body1" {...props} />
);

export const Caption: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="caption" {...props} />
);

export const Overline: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
	<Typography variant="overline" {...props} />
);
